import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/src/components/ui/card';
import { Button } from '@/src/components/ui/button';
import { Input } from '@/src/components/ui/input';
import { CheckCircle2, ShieldCheck, AlertCircle, Building, Wallet, CreditCard, Key, Eye, EyeOff, Loader2 } from 'lucide-react';
import { Switch } from '@/src/components/ui/switch';
import { useAuth } from '@/src/contexts/AuthContext';
import { getSupabaseClient } from '@/src/lib/supabase';
import { toast } from 'sonner';

export function PaymentSettingsView() {
  const { currentTenantId } = useAuth();
  const [kycStatus] = useState<'pending' | 'submitted' | 'active'>('active');

  // Gateway status state
  const [loadingGateway, setLoadingGateway] = useState(true);
  const [gatewayStatus, setGatewayStatus] = useState<{ connected?: boolean; key_id_masked?: string; [key: string]: any } | null>(null);
  const [showConnectForm, setShowConnectForm] = useState(false);
  const [keyId, setKeyId] = useState('');
  const [keySecret, setKeySecret] = useState('');
  const [webhookSecret, setWebhookSecret] = useState('');
  const [showSecret, setShowSecret] = useState(false);
  const [savingCredentials, setSavingCredentials] = useState(false);
  const [disconnecting, setDisconnecting] = useState(false);

  const fetchGatewayStatus = async () => {
    if (!currentTenantId) return;
    setLoadingGateway(true);
    const supabase = getSupabaseClient();
    if (!supabase) {
      setLoadingGateway(false);
      return;
    }
    try {
      const { data, error } = await supabase.rpc('get_merchant_gateway_status', {
        p_tenant_id: currentTenantId
      });
      if (error) {
        console.warn('get_merchant_gateway_status error:', error);
        setGatewayStatus({ connected: false });
      } else {
        setGatewayStatus(data || { connected: false });
      }
    } catch (err) {
      console.warn('Error fetching gateway status:', err);
      setGatewayStatus({ connected: false });
    } finally {
      setLoadingGateway(false);
    }
  };

  useEffect(() => {
    fetchGatewayStatus();
  }, [currentTenantId]);

  const handleSaveCredentials = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!keyId.trim() || !keySecret.trim()) {
      toast.error('Razorpay Key ID aur Key Secret dono bharna zaroori hai');
      return;
    }
    if (!currentTenantId) {
      toast.error('Tenant identification missing');
      return;
    }
    const supabase = getSupabaseClient();
    if (!supabase) return;

    setSavingCredentials(true);
    try {
      const { error } = await supabase.rpc('save_merchant_gateway_credentials', {
        p_tenant_id: currentTenantId,
        p_key_id: keyId.trim(),
        p_key_secret: keySecret.trim(),
        p_webhook_secret: webhookSecret.trim() || null
      });

      if (error) {
        toast.error(error.message || 'Credentials save nahi ho sake');
      } else {
        toast.success('Razorpay account successfully connect ho gaya!');
        setShowConnectForm(false);
        setKeyId('');
        setKeySecret('');
        setWebhookSecret('');
        await fetchGatewayStatus();
      }
    } catch (err: any) {
      toast.error(err.message || 'Failed to save gateway credentials');
    } finally {
      setSavingCredentials(false);
    }
  };

  const handleDisconnectGateway = async () => {
    if (!currentTenantId) return;
    const confirmDisconnect = window.confirm(
      'Kya aap apna custom Razorpay gateway disconnect karke wapas BahiBox ke shared account par switch karna chahte hain?'
    );
    if (!confirmDisconnect) return;

    setDisconnecting(true);
    const supabase = getSupabaseClient();
    if (!supabase) {
      setDisconnecting(false);
      return;
    }

    try {
      const { error } = await supabase.rpc('disable_merchant_gateway_credentials', {
        p_tenant_id: currentTenantId
      });

      if (error) {
        toast.error(error.message || 'Disconnect nahi ho saka');
      } else {
        toast.success('BahiBox shared Razorpay account par switch ho gaye!');
        await fetchGatewayStatus();
      }
    } catch (err: any) {
      toast.error(err.message || 'Failed to disable custom gateway');
    } finally {
      setDisconnecting(false);
    }
  };

  const isConnected = gatewayStatus?.connected === true;

  return (
    <div className="space-y-6 max-w-4xl">
      <div>
        <h2 className="text-xl font-bold dark:text-white">Payment Settings & KYC</h2>
        <p className="text-sm text-slate-500 dark:text-slate-400">Onboard your bank account to receive payments directly via Razorpay Route.</p>
      </div>

      {/* KYC Card */}
      <Card className="border-none shadow-sm overflow-hidden">
        <div className={`h-2 w-full ${kycStatus === 'active' ? 'bg-green-500' : kycStatus === 'submitted' ? 'bg-blue-500' : 'bg-amber-500'}`} />
        <CardHeader className="border-b bg-slate-50 dark:bg-slate-900/50 pb-4">
          <div className="flex justify-between items-center">
            <div>
              <CardTitle className="flex items-center gap-2">
                <ShieldCheck className={kycStatus === 'active' ? 'text-green-600' : 'text-slate-400'} /> 
                Account Verification (KYC)
              </CardTitle>
              <CardDescription className="mt-1">
                Required for auto-settlement of consumer payments to your bank account.
              </CardDescription>
            </div>
            {kycStatus === 'active' && (
              <span className="px-3 py-1 bg-green-100 text-green-800 text-xs font-bold rounded-full flex items-center gap-1 border border-green-200">
                <CheckCircle2 size={14} /> KYC Verified & Active
              </span>
            )}
            {kycStatus === 'pending' && (
              <span className="px-3 py-1 bg-amber-100 text-amber-800 text-xs font-bold rounded-full flex items-center gap-1 border border-amber-200">
                <AlertCircle size={14} /> Pending KYC
              </span>
            )}
          </div>
        </CardHeader>

        <CardContent className="p-6 space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="space-y-4">
              <h3 className="text-sm font-semibold text-slate-700 dark:text-slate-300 flex items-center gap-2 border-b pb-2">
                <Building size={16} /> Business Details
              </h3>
              <div className="space-y-3">
                <div className="space-y-1">
                  <label className="text-xs font-medium text-slate-500 dark:text-slate-400">Legal Business Name</label>
                  <Input value="Sharma General Store" disabled={kycStatus !== 'pending'} className="bg-slate-50 dark:bg-slate-900" />
                </div>
                <div className="space-y-1">
                  <label className="text-xs font-medium text-slate-500 dark:text-slate-400">Business PAN</label>
                  <Input value="ABCDE1234F" disabled={kycStatus !== 'pending'} className="bg-slate-50 dark:bg-slate-900 font-mono" />
                </div>
              </div>
            </div>

            <div className="space-y-4">
              <h3 className="text-sm font-semibold text-slate-700 dark:text-slate-300 flex items-center gap-2 border-b pb-2">
                <Wallet size={16} /> Settlement Bank Account
              </h3>
              <div className="space-y-3">
                <div className="space-y-1">
                  <label className="text-xs font-medium text-slate-500 dark:text-slate-400">Account Number</label>
                  <Input value={kycStatus === 'active' ? "•••• •••• 9876" : ""} disabled={kycStatus !== 'pending'} className="bg-slate-50 dark:bg-slate-900 font-mono" />
                </div>
                <div className="space-y-1">
                  <label className="text-xs font-medium text-slate-500 dark:text-slate-400">IFSC Code</label>
                  <Input value={kycStatus === 'active' ? "SBIN0001234" : ""} disabled={kycStatus !== 'pending'} className="bg-slate-50 dark:bg-slate-900 font-mono" />
                </div>
                <div className="space-y-1">
                  <label className="text-xs font-medium text-slate-500 dark:text-slate-400">Account Holder Name</label>
                  <Input value="Sharma General Store" disabled={kycStatus !== 'pending'} className="bg-slate-50 dark:bg-slate-900" />
                </div>
              </div>
            </div>
          </div>

          {kycStatus === 'active' && (
            <div className="mt-6 pt-6 border-t flex items-center justify-between">
              <div>
                <h4 className="font-semibold">Auto-Settlement</h4>
                <p className="text-sm text-slate-500 dark:text-slate-400">Automatically transfer funds to your bank account daily.</p>
              </div>
              <Switch checked={true} onCheckedChange={() => {}} />
            </div>
          )}

          {kycStatus === 'pending' && (
            <div className="pt-4 flex justify-end">
              <Button className="px-8">Submit for KYC Verification</Button>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Task B: Payment Gateway Card */}
      <Card className="border-none shadow-sm overflow-hidden">
        <CardHeader className="border-b pb-4 bg-slate-50 dark:bg-slate-900/50">
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
            <div>
              <CardTitle className="flex items-center gap-2">
                <CreditCard className="text-indigo-600 dark:text-indigo-400" size={20} />
                Payment Gateway
              </CardTitle>
              <CardDescription className="mt-1">
                Apna khud ka Razorpay account connect karein ya BahiBox ka shared payment gateway use karein.
              </CardDescription>
            </div>
            {loadingGateway ? (
              <span className="flex items-center gap-2 text-xs text-slate-400">
                <Loader2 className="w-3.5 h-3.5 animate-spin" /> Checking status...
              </span>
            ) : isConnected ? (
              <span className="px-3 py-1 bg-emerald-100 text-emerald-800 dark:bg-emerald-900/30 dark:text-emerald-300 text-xs font-bold rounded-full flex items-center gap-1 border border-emerald-200 dark:border-emerald-800">
                <CheckCircle2 size={13} /> Connected: {gatewayStatus?.key_id_masked || 'Active'}
              </span>
            ) : (
              <span className="px-3 py-1 bg-blue-50 text-blue-800 dark:bg-blue-900/30 dark:text-blue-300 text-xs font-bold rounded-full flex items-center gap-1 border border-blue-200 dark:border-blue-800">
                Currently using BahiBox's shared Razorpay account
              </span>
            )}
          </div>
        </CardHeader>

        <CardContent className="p-6 space-y-5">
          {loadingGateway ? (
            <div className="py-6 flex items-center justify-center gap-2 text-slate-400">
              <Loader2 className="w-5 h-5 animate-spin" />
              <span>Loading gateway configuration...</span>
            </div>
          ) : isConnected ? (
            <div className="space-y-4">
              <div className="p-4 rounded-xl bg-emerald-50 dark:bg-emerald-950/20 border border-emerald-200 dark:border-emerald-800/40 flex flex-col sm:flex-row sm:items-center justify-between gap-4">
                <div className="space-y-1">
                  <div className="flex items-center gap-2 text-emerald-900 dark:text-emerald-200 font-semibold">
                    <Key size={16} />
                    <span>Razorpay Live Credentials Linked</span>
                  </div>
                  <p className="text-xs text-emerald-700 dark:text-emerald-400">
                    Key ID: <span className="font-mono font-bold">{gatewayStatus?.key_id_masked}</span>
                  </p>
                  <p className="text-xs text-slate-600 dark:text-slate-300">
                    Aapka apna Razorpay account active hai. Customers se aane wala paisa seedha aapke bank account mein credit hoga.
                  </p>
                </div>
                <Button
                  variant="outline"
                  className="border-red-200 text-red-600 hover:bg-red-50 dark:hover:bg-red-950/40 whitespace-nowrap self-start sm:self-center"
                  onClick={handleDisconnectGateway}
                  disabled={disconnecting}
                >
                  {disconnecting ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" /> Disconnecting...
                    </>
                  ) : (
                    'Disconnect / Use BahiBox Account Instead'
                  )}
                </Button>
              </div>
            </div>
          ) : (
            <div className="space-y-4">
              <div className="p-4 rounded-xl bg-slate-50 dark:bg-slate-900 border border-slate-200 dark:border-slate-800 flex flex-col sm:flex-row sm:items-center justify-between gap-4">
                <div className="space-y-1">
                  <p className="text-sm font-semibold text-slate-800 dark:text-slate-200">
                    BahiBox Shared Gateway
                  </p>
                  <p className="text-xs text-slate-500 dark:text-slate-400">
                    Payments made by your customers are collected via BahiBox's shared gateway and routed to your merchant wallet.
                  </p>
                </div>
                {!showConnectForm && (
                  <Button
                    className="bg-indigo-600 hover:bg-indigo-700 text-white whitespace-nowrap self-start sm:self-center"
                    onClick={() => setShowConnectForm(true)}
                  >
                    Connect My Own Razorpay Account
                  </Button>
                )}
              </div>

              {showConnectForm && (
                <form onSubmit={handleSaveCredentials} className="p-5 rounded-xl border border-indigo-100 dark:border-indigo-900/40 bg-indigo-50/40 dark:bg-indigo-950/20 space-y-4">
                  <div className="flex items-center justify-between border-b border-indigo-100 dark:border-indigo-900/40 pb-2">
                    <h4 className="text-sm font-bold text-slate-900 dark:text-slate-100 flex items-center gap-2">
                      <Key size={16} className="text-indigo-600" /> Connect Razorpay API Credentials
                    </h4>
                    <button
                      type="button"
                      className="text-xs text-slate-400 hover:text-slate-600 dark:hover:text-slate-200"
                      onClick={() => setShowConnectForm(false)}
                    >
                      Cancel
                    </button>
                  </div>

                  <div className="space-y-3">
                    <div className="space-y-1">
                      <label className="text-xs font-semibold text-slate-700 dark:text-slate-300">
                        Razorpay Key ID <span className="text-red-500">*</span>
                      </label>
                      <Input
                        type="text"
                        placeholder="rzp_live_..."
                        value={keyId}
                        onChange={(e) => setKeyId(e.target.value)}
                        required
                        className="font-mono bg-white dark:bg-slate-900"
                      />
                      <p className="text-[11px] text-slate-500">Found in your Razorpay Dashboard → Settings → API Keys.</p>
                    </div>

                    <div className="space-y-1">
                      <div className="flex items-center justify-between">
                        <label className="text-xs font-semibold text-slate-700 dark:text-slate-300">
                          Razorpay Key Secret <span className="text-red-500">*</span>
                        </label>
                        <button
                          type="button"
                          onClick={() => setShowSecret(!showSecret)}
                          className="text-xs text-slate-500 hover:text-slate-700 dark:hover:text-slate-300 flex items-center gap-1"
                        >
                          {showSecret ? <EyeOff size={12} /> : <Eye size={12} />}
                          {showSecret ? 'Hide' : 'Show'}
                        </button>
                      </div>
                      <Input
                        type={showSecret ? "text" : "password"}
                        placeholder="Enter your secret key"
                        value={keySecret}
                        onChange={(e) => setKeySecret(e.target.value)}
                        required
                        className="font-mono bg-white dark:bg-slate-900"
                      />
                    </div>

                    <div className="space-y-1">
                      <label className="text-xs font-semibold text-slate-700 dark:text-slate-300">
                        Webhook Secret <span className="text-slate-400 font-normal">(Optional)</span>
                      </label>
                      <Input
                        type="password"
                        placeholder="Optional webhook secret"
                        value={webhookSecret}
                        onChange={(e) => setWebhookSecret(e.target.value)}
                        className="font-mono bg-white dark:bg-slate-900"
                      />
                      <p className="text-[11px] text-slate-500">
                        For verifying webhook signatures from your Razorpay Dashboard webhook configuration.
                      </p>
                    </div>
                  </div>

                  <div className="flex items-center gap-3 pt-2">
                    <Button
                      type="submit"
                      className="bg-indigo-600 hover:bg-indigo-700 text-white"
                      disabled={savingCredentials}
                    >
                      {savingCredentials ? (
                        <>
                          <Loader2 className="w-4 h-4 mr-2 animate-spin" /> Saving Credentials...
                        </>
                      ) : (
                        'Save & Connect Gateway'
                      )}
                    </Button>
                    <Button
                      type="button"
                      variant="outline"
                      onClick={() => setShowConnectForm(false)}
                      disabled={savingCredentials}
                    >
                      Cancel
                    </Button>
                  </div>
                </form>
              )}
            </div>
          )}

          {/* Mandatory note as requested */}
          <div className="pt-2 border-t border-slate-100 dark:border-slate-800">
            <p className="text-xs text-slate-600 dark:text-slate-400 flex items-center gap-1.5">
              <span className="font-semibold text-slate-700 dark:text-slate-300">Note:</span>
              Apna Razorpay account connect karne ke baad customer ka paisa seedha aapke bank account mein jaayega.
            </p>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
